import {createClient} from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
const layoutPreviewBucket = process.env.REACT_APP_SUPABASE_LAYOUT_PREVIEW_BUCKET || 'layout-previews'

const isConfigured = Boolean(supabaseUrl && supabaseAnonKey)
const supabase = isConfigured ? createClient(supabaseUrl, supabaseAnonKey) : null

const ensureClient = () => {
    if (!supabase) {
        throw new Error('Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.')
    }

    return supabase
}

export const isCommunityEnabled = () => isConfigured

export const ensureAnonymousSession = async () => {
    const client = ensureClient()
    const {data: sessionData, error: sessionError} = await client.auth.getSession()
    if (sessionError) {
        throw sessionError
    }

    if (sessionData.session) {
        return sessionData.session
    }

    const {data, error} = await client.auth.signInAnonymously()
    if (error) {
        throw error
    }

    return data.session
}

export const getAuthenticatedUserId = async () => {
    const client = ensureClient()
    const {data, error} = await client.auth.getUser()
    if (error) {
        throw error
    }

    return data.user?.id ?? null
}

export const uploadLayoutPreview = async ({
    previewBlob,
    userId,
    fileExtension = 'png',
    contentType = 'image/png'
}) => {
    const client = ensureClient()
    const safeUserId = (userId || 'anonymous').replace(/[^a-zA-Z0-9-]/g, '')
    const normalizedExtension = (fileExtension || 'png').replace(/[^a-z0-9]/gi, '').toLowerCase() || 'png'
    const objectPath = `${safeUserId}/${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${normalizedExtension}`

    const {error: uploadError} = await client.storage
        .from(layoutPreviewBucket)
        .upload(objectPath, previewBlob, {
            contentType,
            upsert: false
        })

    if (uploadError) {
        throw uploadError
    }

    const {data} = client.storage.from(layoutPreviewBucket).getPublicUrl(objectPath)
    if (!data?.publicUrl) {
        throw new Error('Preview upload completed but URL could not be generated.')
    }

    return {
        objectPath,
        publicUrl: data.publicUrl
    }
}

const fetchCommunityLayoutsFromSource = async ({dimensions, source}) => {
    const client = ensureClient()
    const {data, error} = await client
        .from(source)
        .select('id,title,dimensions,layout_values,preview_image_url,is_community,vote_count,created_at')
        .eq('dimensions', dimensions)
        .order('vote_count', {ascending: false})
        .order('created_at', {ascending: false})
        .limit(300)

    if (error) {
        throw error
    }

    return data || []
}

export const fetchPromotedCommunityLayouts = async ({dimensions}) => (
    fetchCommunityLayoutsFromSource({
        dimensions,
        source: 'promoted_layout_bank'
    })
)

export const fetchApprovedCommunityLayouts = async ({dimensions}) => (
    fetchCommunityLayoutsFromSource({
        dimensions,
        source: 'approved_layout_bank'
    })
)

export const isCurrentUserAdmin = async () => {
    const client = ensureClient()
    const {data: userData, error: userError} = await client.auth.getUser()
    if (userError) {
        throw userError
    }

    const userId = userData.user?.id
    if (!userId) {
        return false
    }

    const {data, error} = await client.rpc('is_admin', {check_user_id: userId})
    if (error) {
        throw error
    }

    return Boolean(data)
}

export const fetchPendingLayouts = async ({dimensions}) => {
    const client = ensureClient()
    const {data, error} = await client
        .from('layout_submissions')
        .select('id,title,dimensions,layout_values,preview_image_url,status,vote_count,created_at')
        .eq('dimensions', dimensions)
        .eq('status', 'pending')
        .order('created_at', {ascending: false})
        .limit(300)

    if (error) {
        throw error
    }

    return data || []
}

export const deletePendingLayoutSubmission = async submissionId => {
    const client = ensureClient()
    const {data, error} = await client
        .from('layout_submissions')
        .delete()
        .eq('id', submissionId)
        .eq('status', 'pending')
        .select('id')

    if (error) {
        throw error
    }

    if (!Array.isArray(data) || data.length === 0) {
        throw new Error('Pending design not found or already moderated.')
    }

    return data[0]
}

export const fetchCurrentUserVotes = async submissionIds => {
    if (!submissionIds.length) {
        return []
    }

    const client = ensureClient()
    const {data, error} = await client
        .from('layout_votes')
        .select('submission_id')
        .in('submission_id', submissionIds)

    if (error) {
        throw error
    }

    return data || []
}

export const submitLayout = async ({title, previewImageUrl, dimensions, layoutValues}) => {
    const client = ensureClient()
    const {data, error} = await client
        .from('layout_submissions')
        .insert({
            title,
            preview_image_url: previewImageUrl,
            dimensions,
            layout_values: layoutValues
        })
        .select('id,title,dimensions,layout_values,preview_image_url,status,created_at')
        .single()

    if (error) {
        throw error
    }

    return data
}

export const toggleLayoutVote = async submissionId => {
    const client = ensureClient()
    const {data, error} = await client.rpc('toggle_layout_vote', {target_submission_id: submissionId})
    if (error) {
        throw error
    }

    const voteState = Array.isArray(data) ? data[0] : data
    if (!voteState) {
        throw new Error('Unable to read vote state response.')
    }

    return voteState
}

export const moderateLayoutSubmission = async ({submissionId, nextStatus}) => {
    const client = ensureClient()
    const {data, error} = await client.rpc('moderate_layout_submission', {
        target_submission_id: submissionId,
        next_status: nextStatus
    })
    if (error) {
        throw error
    }

    return data
}
